<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import type { DateRange } from 'radix-vue'
  import {
    Trash2,
    ExternalLink,
    MoreHorizontal,
    Settings,
    PhoneCall,
    Mail,
    Calendar,
    X,
    Search,
    ChevronDown,
    Check,
    Copy,
    Plus,
    Loader2
  } from 'lucide-vue-next'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Badge } from '@/components/ui/badge'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse, FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import {
    CallsService,
    type TCall,
    type CallsListResponse,
    type CustomerCallResult,
    OrganisationsService,
    type CustomerOrganisation,
    ClassificationEnum,
    PhoneNumbersService,
    type CustomerPhoneNumber,
    UsersService,
    type TUser,
    RoleEnum,
    CasesService
  } from '@/types/generated'
  import { useRoute, useRouter } from 'vue-router'
  import { useTranslation } from '@/composables/useTranslation'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import { ROUTES } from '@/router/routes.ts'
  import {
    getPhoneAiWarningTier,
    resolvePhoneAiIncludedMinutes,
    shouldDisplayPhoneAiUsage
  } from '@/utils/phoneAiUsage'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import HelpLink from '@/components/app/HelpLink.vue'
  import { KNOWLEDGE_BASE_URLS } from '@/constants/knowledgeBaseUrls'
  import PhonebotSetupDialog from '@/components/app/calls/PhonebotSetupDialog.vue'
  import CallListNoteDialog from '@/components/app/calls/CallListNoteDialog.vue'
  import UsageProgressBar from '@/components/app/calls/UsageProgressBar.vue'
  import UsageWarningBanner from '@/components/app/calls/UsageWarningBanner.vue'
  import ClerkSelectionPopover from '@/components/app/cases/ClerkSelectionPopover.vue'
  import type { ClerkOption } from '@/components/app/cases/ClerkSelectionPopover.vue'
  import PhonePlusIcon from '@/components/icons/PhonePlusIcon.vue'
  import PhoneCheckIcon from '@/components/icons/PhoneCheckIcon.vue'
  import CustomerService from '@/components/svg/Illustrations/CustomerService.vue'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { RangeCalendar } from '@/components/ui/range-calendar'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import type { CalendarDate } from '@internationalized/date'
  import { genericErrorToast } from '@/lib/toasts'
  import { getInitials } from '@/lib/utils'
  import { useToast } from '@/components/ui/toast'
  import { createLatestRequestGate } from '@/utils/latestRequestGate'
  import { shouldShowCallListTableSection } from './callListTableSectionVisibility'
  import { fetchCallTabCounts } from './callTabCounts'

  const { toast } = useToast()
  const { trackEvent } = useMetrics()

  const route = useRoute()
  const router = useRouter()

  const { translateLawField } = useLawFieldTranslation()
  const { translateLawTopic } = useLawTopicTranslation()

  const { t, locale } = useTranslation({
    en: {
      Calls: 'Calls',
      TimeStamp: 'Time Stamp',
      Type: 'Type',
      Name: 'Name',
      Phone: 'Phone',
      FieldTopic: 'Field/Topic',
      Duration: 'Duration',
      Recording: 'Recording',
      Action: 'Action',
      ViewInquiry: 'Inquiry',
      Inquiry: 'Show inquiry',
      ViewDetails: 'Show details',
      Unknown: 'Unknown',
      pageDescription: 'Manage incoming calls and inquiries',
      NotCompleted: 'Not Completed',
      ExistingClient: 'Existing Client',
      NewClient: 'New Client',
      GeneralEnquiry: 'General Enquiry',
      Question: 'Question',
      LawFirmPartner: 'Law Firm Partner',
      Delete: 'Delete',
      HasInquiry: 'Has associated inquiry',
      CallForwarded: 'Call Forwarded',
      WithoutMessage: 'Without Message',
      Court: 'Court',
      Spam: 'Spam',
      AllTypes: 'All Types',
      LawField: 'Law Field',
      YourPhoneNumber: 'Your Phone Number',
      YourPhoneNumbers: 'Your Phone Numbers',
      GoToSettings: 'Go to Settings',
      Clerk: 'Clerk',
      Status: 'Status',
      statusOpen: 'Open',
      statusInProgress: 'In progress',
      statusHandled: 'Handled',
      notAssigned: 'Not assigned',
      assignClerk: 'Assign clerk',
      addClerk: '+ Add a clerk',
      maxClerksReached: 'No more than 3 clerks can be assigned.',
      loading: 'Loading...',
      emptyStateTitle: 'Set up your phone bot to get started',
      emptyStateDescription:
        'Your phone bot is not yet configured.\nComplete the setup to automatically handle calls.',
      setupNow: 'Set up now',
      TestCall: 'Test call',
      phoneDialogDescription: 'Call your number to test the phone AI yourself.',
      moreNumbers: '+{count} more',
      contactSupport: 'Contact support',
      allClerks: 'All Clerks',
      allLawFields: 'All Law Fields',
      all: 'All',
      clearAll: 'Clear all',
      timeframe: 'Timeframe',
      search: 'Search',
      NewCalls: 'New Calls',
      Closed: 'Closed',
      noCallsFound: 'No calls found',
      noCallsYet: "You don't have any calls yet.",
      noResultsWithFilters: 'The current filters return no results.',
      resetFilters: 'Reset filters',
      note: 'Note',
      addNote: '+ Add a note',
      editNote: 'Edit note',
      enterNote: 'Enter note...',
      save: 'Save',
      cancel: 'Cancel',
      noteSaveError: 'Could not save the note. Please try again.',
      toInquiry: 'To inquiry',
      toInquiryNamed: 'To inquiry {name}',
      createInquiry: 'Create inquiry',
      completeCall: 'Mark as closed',
      markAsNew: 'Mark as new',
      learnMorePhoneAi: 'Learn more about the phone AI',
      contactSupportTitle: 'Contact support',
      contactSupportDescription: 'You can contact JUPUS support at {email}',
      upgradePlanTitle: 'Contact support',
      upgradePlanDescription: 'You can upgrade your plan by contacting JUPUS support at {email}',
      copyEmail: 'Copy email',
      copyEmailDone: 'Email copied',
      openEmailApp: 'Open default email app'
    },
    de: {
      Calls: 'Anrufe',
      TimeStamp: 'Eingegangen',
      Type: 'Typ',
      Name: 'Name',
      Phone: 'Telefonnummer',
      FieldTopic: 'Rechtsgebiet/Thema',
      Duration: 'Dauer',
      Recording: 'Aufnahme',
      Action: 'Aktion',
      ViewInquiry: 'Anfrage',
      Inquiry: 'Zur Anfrage',
      ViewDetails: 'Zum Anruf',
      Unknown: 'Unbekannt',
      pageDescription: 'Verwalten Sie eingehende Anrufe und Anfragen',
      NotCompleted: 'Unvollständig',
      ExistingClient: 'Bestandsmandant',
      NewClient: 'Neumandant',
      GeneralEnquiry: 'Allgemeine Anfrage',
      Question: 'Frage',
      LawFirmPartner: 'Geschäftspartner',
      Delete: 'Löschen',
      HasInquiry: 'Hat zugeordnete Anfrage',
      CallForwarded: 'Anruf weitergeleitet',
      WithoutMessage: 'Ohne Nachricht',
      Court: 'Gericht',
      Spam: 'Spam',
      AllTypes: 'Alle Typen',
      LawField: 'Rechtsgebiet',
      YourPhoneNumber: 'Ihre Telefonnummer',
      YourPhoneNumbers: 'Ihre Telefonnummern',
      GoToSettings: 'Zu den Einstellungen',
      Clerk: 'Sachbearbeiter',
      Status: 'Status',
      statusOpen: 'Offen',
      statusInProgress: 'In Bearbeitung',
      statusHandled: 'Erledigt',
      notAssigned: 'Nicht zugewiesen',
      assignClerk: 'Sachbearbeiter zuweisen',
      addClerk: '+ Sachbearbeiter',
      maxClerksReached: 'Es können maximal 3 Sachbearbeiter zugewiesen werden.',
      loading: 'Laden...',
      emptyStateTitle: 'Richten Sie Ihren Phonebot ein, um zu beginnen',
      emptyStateDescription:
        'Ihr Phonebot ist noch nicht konfiguriert.\nVervollständigen Sie die Einrichtung, um Anrufe automatisch zu bearbeiten.',
      setupNow: 'Jetzt einrichten',
      TestCall: 'Testanruf',
      phoneDialogDescription: 'Rufen Sie Ihre Nummer an, um die Telefon-KI selbst zu testen.',
      moreNumbers: '+{count} mehr',
      contactSupport: 'Support kontaktieren',
      allClerks: 'Alle Sachbearbeiter',
      allLawFields: 'Alle Rechtsgebiete',
      all: 'Alle',
      clearAll: 'Alle löschen',
      timeframe: 'Zeitraum',
      search: 'Suchen',
      NewCalls: 'Neue Anrufe',
      Closed: 'Abgeschlossen',
      noCallsFound: 'Keine Anrufe gefunden',
      noCallsYet: 'Sie haben noch keine Anrufe.',
      noResultsWithFilters: 'Die aktuellen Filter liefern keine Ergebnisse.',
      resetFilters: 'Filter zurücksetzen',
      note: 'Notiz',
      addNote: '+ Notiz',
      editNote: 'Notiz bearbeiten',
      enterNote: 'Notiz eingeben...',
      save: 'Speichern',
      cancel: 'Abbrechen',
      noteSaveError: 'Die Notiz konnte nicht gespeichert werden. Bitte erneut versuchen.',
      toInquiry: 'Zur Anfrage',
      toInquiryNamed: 'Zur Anfrage {name}',
      createInquiry: 'Anfrage anlegen',
      completeCall: 'Abschließen',
      markAsNew: 'Als neu festlegen',
      learnMorePhoneAi: 'Mehr über die Telefon-KI erfahren',
      contactSupportTitle: 'Support kontaktieren',
      contactSupportDescription: 'Sie können den JUPUS-Support unter {email} kontaktieren',
      upgradePlanTitle: 'Support kontaktieren',
      upgradePlanDescription:
        'Sie können den JUPUS-Support unter {email} kontaktieren, um Ihren Tarif aufzuwerten',
      copyEmail: 'E-Mail kopieren',
      copyEmailDone: 'E-Mail kopiert',
      openEmailApp: 'E-Mail-App öffnen'
    },
    fr: {
      Calls: 'Appels',
      TimeStamp: 'Horodatage',
      Type: 'Type',
      Name: 'Nom',
      Phone: 'Téléphone',
      FieldTopic: 'Domaine/Sujet',
      Duration: 'Durée',
      Recording: 'Enregistrement',
      Action: 'Action',
      ViewInquiry: 'Demande',
      Inquiry: 'Voir la demande',
      ViewDetails: 'Voir les détails',
      Unknown: 'Inconnu',
      pageDescription: 'Gérer les appels entrants et les demandes',
      NotCompleted: 'Non terminé',
      ExistingClient: 'Client existant',
      NewClient: 'Nouveau client',
      GeneralEnquiry: 'Demande générale',
      Question: 'Question',
      LawFirmPartner: 'Partenaire du cabinet',
      Delete: 'Supprimer',
      HasInquiry: 'A une demande associée',
      CallForwarded: 'Appel transféré',
      WithoutMessage: 'Sans message',
      Court: 'Tribunal',
      Spam: 'Spam',
      AllTypes: 'Tous les types',
      LawField: 'Domaine juridique',
      YourPhoneNumber: 'Votre numéro de téléphone',
      YourPhoneNumbers: 'Vos numéros de téléphone',
      GoToSettings: 'Aller aux paramètres',
      Clerk: 'Collaborateur',
      Status: 'Statut',
      statusOpen: 'Ouvert',
      statusInProgress: 'En cours',
      statusHandled: 'Traité',
      notAssigned: 'Non attribué',
      assignClerk: 'Attribuer un collaborateur',
      addClerk: '+ Collaborateur',
      maxClerksReached: 'Pas plus de 3 collaborateurs ne peuvent être attribués.',
      loading: 'Chargement...',
      emptyStateTitle: 'Configurez votre bot téléphonique pour commencer',
      emptyStateDescription:
        "Votre bot téléphonique n'est pas encore configuré.\nComplétez la configuration pour traiter automatiquement les appels.",
      setupNow: 'Configurer maintenant',
      TestCall: 'Appel test',
      phoneDialogDescription: "Appelez votre numéro pour tester vous-même l'IA téléphonique.",
      moreNumbers: '+{count} de plus',
      contactSupport: 'Contacter le support',
      allClerks: 'Tous les collaborateurs',
      allLawFields: 'Tous les domaines juridiques',
      all: 'Tous',
      clearAll: 'Tout effacer',
      timeframe: 'Période',
      search: 'Rechercher',
      NewCalls: 'Nouveaux appels',
      Closed: 'Terminés',
      noCallsFound: 'Aucun appel trouvé',
      noCallsYet: "Vous n'avez pas encore d'appels.",
      noResultsWithFilters: 'Les filtres actuels ne retournent aucun résultat.',
      resetFilters: 'Réinitialiser les filtres',
      note: 'Note',
      addNote: '+ Ajouter une note',
      editNote: 'Modifier la note',
      enterNote: 'Saisir une note...',
      save: 'Enregistrer',
      cancel: 'Annuler',
      noteSaveError: "Impossible d'enregistrer la note. Veuillez réessayer.",
      toInquiry: 'Voir la demande',
      toInquiryNamed: 'Voir la demande {name}',
      createInquiry: 'Créer une demande',
      completeCall: 'Terminer',
      markAsNew: 'Marquer comme nouveau',
      learnMorePhoneAi: "En savoir plus sur l'IA téléphonique",
      contactSupportTitle: 'Contacter le support',
      contactSupportDescription: 'Vous pouvez contacter le support JUPUS à {email}',
      upgradePlanTitle: 'Contacter le support',
      upgradePlanDescription:
        'Vous pouvez mettre à niveau votre forfait en contactant le support JUPUS à {email}',
      copyEmail: "Copier l'email",
      copyEmailDone: 'Email copié',
      openEmailApp: "Ouvrir l'application email"
    }
  })

  const MAX_VISIBLE_NUMBERS = 3
  const showPhoneNumbersDialog = ref(false)
  const isVoiceStandAlone = ref(false)
  // Typed against the generated ``TUser['role']`` union so a schema change to
  // either ``CustomerUser`` or ``CustomerAdminUser`` surfaces as a TS error
  // rather than silently flipping ``isPhoneAiUser`` to false and bringing back
  // the GRO-513 regression with no test coverage.
  const currentUserRole = ref<TUser['role'] | null>(null)
  // Phone-AI-only users do not participate in the case-driven open/handled
  // lifecycle, so their list must not be filtered by call status (otherwise
  // calls auto-flipped to HANDLED on case creation disappear from their view).
  const isPhoneAiUser = computed(() => currentUserRole.value === RoleEnum.PHONE_AI_USER)

  const SUPPORT_EMAIL = 'support@jupus.de'
  const supportDialogOpen = ref(false)
  const supportDialogMode = ref<'contact' | 'upgrade'>('contact')
  const supportEmailCopied = ref(false)

  const isGermanLocale = computed(() => locale.value === 'de')

  const openSupportDialog = (mode: 'contact' | 'upgrade') => {
    supportDialogMode.value = mode
    supportEmailCopied.value = false
    supportDialogOpen.value = true
    if (mode === 'upgrade') {
      trackEvent(MetricEventType.PHONE_AI_UPGRADE_PLAN_CLICKED, {
        entry_point: 'usage_progress_bar',
        tier: currentWarningTier()
      })
    } else {
      trackEvent(MetricEventType.PHONE_AI_CONTACT_SUPPORT_CLICKED, {
        entry_point: 'page_header',
        tier: currentWarningTier()
      })
    }
  }

  const copySupportEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL)
      supportEmailCopied.value = true
      trackEvent(MetricEventType.PHONE_AI_SUPPORT_DIALOG_ACTION, {
        action: 'copy_email',
        mode: supportDialogMode.value
      })
    } catch (error) {
      console.error('Failed to copy support email:', error)
      supportEmailCopied.value = false
      toast({
        title: SUPPORT_EMAIL,
        description: 'Please copy the address manually.',
        variant: 'destructive'
      })
    }
  }

  const openSupportMailApp = () => {
    trackEvent(MetricEventType.PHONE_AI_SUPPORT_DIALOG_ACTION, {
      action: 'open_mail_app',
      mode: supportDialogMode.value
    })
    window.location.href = `mailto:${SUPPORT_EMAIL}`
  }

  const currentWarningTier = (): string => {
    if (!org.value) return 'none'
    return getPhoneAiWarningTier(
      org.value.phonebot_self_service_current_period_used_minutes,
      phoneAiIncludedMinutes.value
    )
  }

  const openTestCallDialog = (entryPoint: 'test_call_button' | 'more_numbers_link') => {
    showPhoneNumbersDialog.value = true
    trackEvent(MetricEventType.PHONE_AI_TEST_CALL_DIALOG_OPENED, {
      entry_point: entryPoint,
      phone_numbers_count: phoneNumbers.value.length
    })
  }

  const handleOwnNumberClicked = (
    phoneNumber: CustomerPhoneNumber,
    entryPoint: 'phone_numbers_card' | 'test_call_dialog'
  ) => {
    trackEvent(MetricEventType.PHONE_AI_OWN_NUMBER_CLICKED, {
      entry_point: entryPoint,
      phone_number_uuid: phoneNumber.uuid
    })
  }
  const isLoading = ref(false)
  const selectedLawField = ref('')
  const selectedClassifications = ref('')
  const selectedClerk = ref('')
  const selectedStatus = ref('')
  const searchQuery = ref('')
  const tableRef = ref()
  const org = ref<CustomerOrganisation>()
  const phoneNumbers = ref<CustomerPhoneNumber[]>([])
  const orgUsers = computed(() => org.value?.users ?? [])
  const totalCalls = ref<number | null>(null)

  // Call status tab: 'new' shows open/in_progress, 'closed' shows handled
  const callStatusTab = ref<'new' | 'closed'>('new')
  const newCallsCount = ref<number>(0)
  const closedCallsCount = ref<number>(0)

  /** Voice standalone: show call list UI if the org has calls or already has phone numbers configured. */
  const showVoiceStandaloneCallList = computed(
    () => newCallsCount.value > 0 || closedCallsCount.value > 0 || phoneNumbers.value.length > 0
  )

  const orgFetchSettled = ref(false)
  const phoneNumbersFetchSettled = ref(false)
  const tabCountsFetchSettled = ref(false)

  const voiceStandaloneBootstrapComplete = computed(
    () => orgFetchSettled.value && phoneNumbersFetchSettled.value && tabCountsFetchSettled.value
  )

  const dateRange = ref<DateRange>({
    start: undefined,
    end: undefined
  })

  // Dropdown open/search state
  const typeDropdownOpen = ref(false)
  const typeDropdownSearch = ref('')
  const lawFieldDropdownOpen = ref(false)
  const lawFieldDropdownSearch = ref('')
  const clerkDropdownOpen = ref(false)
  const clerkDropdownSearch = ref('')

  const initialPage = computed(() => {
    const page = Number(route.query.page)
    return Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1
  })

  const handlePageChange = (page: number) => {
    router.replace({ query: { ...route.query, page: page > 1 ? page.toString() : undefined } })
  }

  const classificationOptions = [
    { value: ClassificationEnum.EXISTING_CLIENT_CALL_BACK, label: 'ExistingClient' },
    { value: ClassificationEnum.NEW_CLIENT_CALL, label: 'NewClient' },
    { value: ClassificationEnum.GENERAL_ENQUIRY, label: 'GeneralEnquiry' },
    { value: ClassificationEnum.QUESTION, label: 'Question' },
    { value: ClassificationEnum.NOT_COMPLETED, label: 'NotCompleted' },
    { value: ClassificationEnum.LAWFIRM_PARTNER, label: 'LawFirmPartner' },
    { value: ClassificationEnum.CALL_FORWARDED, label: 'CallForwarded' },
    { value: ClassificationEnum.WITHOUT_MESSAGE, label: 'WithoutMessage' },
    { value: ClassificationEnum.COURT, label: 'Court' },
    { value: ClassificationEnum.SPAM, label: 'Spam' }
  ]

  const lawFieldOptions = computed(() => {
    if (!org.value?.law_fields) return []
    return (org.value.law_fields as Array<string>).map((value) => ({
      value,
      title: translateLawField(value)
    }))
  })

  const phoneAiIncludedMinutes = computed(() => resolvePhoneAiIncludedMinutes(org.value))

  const showPhoneAiUsage = computed(() => shouldDisplayPhoneAiUsage(org.value))

  const clerkOptions = computed(() => {
    return [
      { value: '', title: t('allClerks') },
      ...orgUsers.value
        .map((user) => ({
          value: user.uuid,
          title: `${user.first_name} ${user.last_name}`
        }))
        .sort((a, b) => a.title.localeCompare(b.title))
    ]
  })

  // Filtered dropdown options (search within dropdowns)
  const filteredTypeOptions = computed(() => {
    const search = typeDropdownSearch.value.toLowerCase()
    const opts = classificationOptions
      .map((o) => ({ value: o.value, title: t(o.label) }))
      .sort((a, b) => a.title.localeCompare(b.title))
    if (!search) return opts
    return opts.filter((o) => o.title.toLowerCase().includes(search))
  })

  const filteredLawFieldOptions = computed(() => {
    const search = lawFieldDropdownSearch.value.toLowerCase()
    const opts = [{ value: '', title: t('allLawFields') }, ...lawFieldOptions.value]
    if (!search) return opts
    return opts.filter((o) => o.title.toLowerCase().includes(search))
  })

  const filteredClerkOptions = computed(() => {
    const search = clerkDropdownSearch.value.toLowerCase()
    if (!search) return clerkOptions.value
    return clerkOptions.value.filter((o) => o.title.toLowerCase().includes(search))
  })

  // Display labels for selected filter values
  const selectedTypeCount = computed(() => {
    if (!selectedClassifications.value) return 0
    return selectedClassifications.value.split(',').filter(Boolean).length
  })

  const selectedTypeLabel = computed(() => {
    if (!selectedClassifications.value) return t('all')
    const selected = selectedClassifications.value.split(',').filter(Boolean)
    const first = classificationOptions.find((o) => o.value === selected[0])
    return first ? t(first.label) : t('all')
  })

  const selectedLawFieldLabel = computed(() => {
    if (!selectedLawField.value) return t('all')
    const opt = lawFieldOptions.value.find((o) => o.value === selectedLawField.value)
    return opt?.title || t('all')
  })

  const selectedClerkLabel = computed(() => {
    if (!selectedClerk.value) return t('all')
    const opt = clerkOptions.value.find((o) => o.value === selectedClerk.value)
    return opt?.title || t('all')
  })

  const hasActiveFilters = computed(() => {
    return (
      selectedClassifications.value !== '' ||
      selectedLawField.value !== '' ||
      selectedClerk.value !== '' ||
      selectedStatus.value !== '' ||
      searchQuery.value !== '' ||
      dateRange.value.start !== undefined ||
      dateRange.value.end !== undefined
    )
  })

  const showCallListTableSection = computed(() =>
    shouldShowCallListTableSection({
      isVoiceStandAlone: isVoiceStandAlone.value,
      voiceStandaloneBootstrapComplete: voiceStandaloneBootstrapComplete.value,
      isLoading: isLoading.value,
      totalCalls: totalCalls.value,
      hasActiveFilters: hasActiveFilters.value,
      showVoiceStandaloneCallList: showVoiceStandaloneCallList.value
    })
  )

  const clearFilters = () => {
    selectedClassifications.value = ''
    selectedLawField.value = ''
    selectedClerk.value = ''
    selectedStatus.value = ''
    searchQuery.value = ''
    dateRange.value = { start: undefined, end: undefined }
  }

  // Selection handlers
  const selectType = (value: string) => {
    const current = selectedClassifications.value
      ? selectedClassifications.value.split(',').filter(Boolean)
      : []
    if (current.includes(value)) {
      const next = current.filter((v) => v !== value)
      selectedClassifications.value = next.join(',')
    } else {
      selectedClassifications.value = [...current, value].join(',')
    }
  }

  const toggleAllTypes = () => {
    if (selectedTypeCount.value === classificationOptions.length) {
      selectedClassifications.value = ''
    } else {
      selectedClassifications.value = classificationOptions.map((o) => o.value).join(',')
    }
  }

  const clearTypes = () => {
    selectedClassifications.value = ''
  }

  const isTypeSelected = (value: string) => {
    if (!selectedClassifications.value) return false
    return selectedClassifications.value.split(',').includes(value)
  }

  const allTypesChecked = computed(() => selectedTypeCount.value === classificationOptions.length)
  const allTypesIndeterminate = computed(
    () => selectedTypeCount.value > 0 && selectedTypeCount.value < classificationOptions.length
  )

  const selectLawField = (value: string) => {
    selectedLawField.value = value
    lawFieldDropdownOpen.value = false
    lawFieldDropdownSearch.value = ''
  }

  const selectClerk = (value: string) => {
    selectedClerk.value = value
    clerkDropdownOpen.value = false
    clerkDropdownSearch.value = ''
  }

  const formatDateForDisplay = (date: CalendarDate | undefined) => {
    if (!date) return ''
    const jsDate = new Date(date.year, date.month - 1, date.day)
    return jsDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatDateForAPI = (dateObj: any) => {
    if (!dateObj) return undefined
    return `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`
  }

  const dateRangeText = computed(() => {
    if (!dateRange.value.start && !dateRange.value.end) {
      return t('all')
    }
    const startDate = formatDateForDisplay(dateRange.value.start as CalendarDate)
    const endDate = formatDateForDisplay(dateRange.value.end as CalendarDate)
    if (
      dateRange.value.start &&
      dateRange.value.end &&
      dateRange.value.start.year === dateRange.value.end.year &&
      dateRange.value.start.month === dateRange.value.end.month &&
      dateRange.value.start.day === dateRange.value.end.day
    ) {
      return startDate
    }
    return `${startDate} - ${endDate}`
  })

  // Debounced search
  let searchTimeout: ReturnType<typeof setTimeout> | null = null
  watch(searchQuery, () => {
    if (searchTimeout) clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      if (tableRef.value) {
        tableRef.value.resetAndRefresh()
      }
    }, 300)
  })

  watch(
    [selectedLawField, selectedClassifications, selectedClerk, selectedStatus, dateRange],
    () => {
      if (tableRef.value) {
        tableRef.value.resetAndRefresh()
      }
    },
    { deep: true }
  )

  const callsListRequestGate = createLatestRequestGate()

  /** OpenAPI uses optional next/previous; PaginatedTable expects null when absent. */
  const callsListToPaginated = (response: CallsListResponse): PaginatedResponse<TCall> => ({
    count: response.count,
    results: response.results,
    next: response.next ?? null,
    previous: response.previous ?? null
  })

  const fetchCalls = async (options?: FetchDataOptions): Promise<PaginatedResponse<TCall>> => {
    const requestId = callsListRequestGate.startRequest()
    const tabWhenStarted = callStatusTab.value
    try {
      isLoading.value = true

      // Phone-AI-only users see every call regardless of status. The
      // open/handled lifecycle is driven by case creation, which they do not
      // own, so applying a default status filter would silently hide calls
      // that ingress flips to HANDLED when a case is auto-created.
      let statusParam: string | undefined
      if (isPhoneAiUser.value) {
        statusParam = selectedStatus.value || undefined
      } else {
        const tabDefaultStatus = tabWhenStarted === 'new' ? 'open,in_progress' : 'handled'
        const allowedForTab = tabDefaultStatus.split(',')
        statusParam =
          selectedStatus.value && allowedForTab.includes(selectedStatus.value)
            ? selectedStatus.value
            : tabDefaultStatus
      }

      const params: any = {
        limit: options?.limit,
        offset: options?.offset,
        search: searchQuery.value || undefined,
        resultDataLawField: selectedLawField.value || undefined,
        hasResult: true,
        clerk: selectedClerk.value || undefined,
        status: statusParam,
        startDate: formatDateForAPI(dateRange.value.start),
        endDate: formatDateForAPI(dateRange.value.end),
        resultClassification: selectedClassifications.value || undefined
      }

      const response: CallsListResponse = await CallsService.callsList(params)

      if (!callsListRequestGate.isLatestRequest(requestId)) {
        return callsListToPaginated(response)
      }

      totalCalls.value = response.count ?? 0

      if (tabWhenStarted === 'new') {
        newCallsCount.value = response.count ?? 0
      } else {
        closedCallsCount.value = response.count ?? 0
      }

      return callsListToPaginated(response)
    } catch (error) {
      console.error('Error fetching calls:', error)
      throw error
    } finally {
      // Non-latest requests must not clear loading; the current latest request's finally always runs
      // (success or throw) and resets isLoading.
      if (callsListRequestGate.isLatestRequest(requestId)) {
        isLoading.value = false
      }
    }
  }

  const fetchTabCounts = async () => {
    try {
      const counts = await fetchCallTabCounts({ isPhoneAiUser: isPhoneAiUser.value })
      newCallsCount.value = counts.newCallsCount
      closedCallsCount.value = counts.closedCallsCount
    } catch (error) {
      console.error('Error fetching tab counts:', error)
    } finally {
      tabCountsFetchSettled.value = true
    }
  }

  const switchTab = (tab: 'new' | 'closed') => {
    if (callStatusTab.value === tab) return
    callStatusTab.value = tab
    if (tableRef.value) {
      tableRef.value.resetAndRefresh()
    }
  }

  const fetchCurrentOrg = async () => {
    try {
      const [orgResponse, userResponse] = await Promise.all([
        OrganisationsService.organisationsRetrieve({ uuid: 'current' }),
        UsersService.usersRetrieve({ uuid: 'current' })
      ])

      org.value = orgResponse as CustomerOrganisation

      currentUserRole.value = userResponse.role ?? null
      isVoiceStandAlone.value = !org.value.enable_cases || isPhoneAiUser.value
    } catch (error) {
      console.error('Error fetching orgs:', error)
    } finally {
      orgFetchSettled.value = true
    }
  }

  const fetchPhoneNumbers = async () => {
    try {
      const response = await PhoneNumbersService.phoneNumbersList()
      phoneNumbers.value = response.results as CustomerPhoneNumber[]
    } catch (error) {
      console.error('Error fetching phone numbers:', error)
    } finally {
      phoneNumbersFetchSettled.value = true
    }
  }

  const handleGoToSettings = () => {
    router.push(ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_NUMBERS)
  }

  const handlePhonebotSetup = () => {
    handleGoToSettings()
  }

  onMounted(async () => {
    fetchPhoneNumbers()
    // fetchTabCounts depends on the resolved user role (phone-AI users skip
    // the per-status counts), so wait for fetchCurrentOrg before issuing it.
    await fetchCurrentOrg()
    fetchTabCounts()
  })

  const formatDateParts = (timestamp: string) => {
    const date = new Date(timestamp)
    const datePart = date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
    const timePart = date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    })
    return { datePart, timePart }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Transition 'open' -> 'in_progress' when the user opens a call so the unread dot
  // disappears immediately. The call stays in the New Calls tab (filter includes both
  // statuses) and can be reverted via "Mark as new".
  const markCallAsViewed = (call: TCall) => {
    if (call.status !== 'open') return
    tableRef.value?.updateItem(call.uuid, { status: 'in_progress' })
    CallsService.callsPartialUpdate({
      uuid: call.uuid,
      requestBody: { status: 'in_progress' as const }
    }).catch((err) => console.error('Error marking call as viewed:', err))
  }

  const handleJumpToCase = (call: TCall, entryPoint: 'list_icon' | 'context_menu') => {
    if (call.case_uuid) {
      markCallAsViewed(call)
      trackEvent(MetricEventType.PHONE_AI_INQUIRY_OPENED, {
        entry_point: entryPoint,
        call_uuid: call.uuid,
        case_uuid: call.case_uuid
      })
      window.location.href = ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(':uuid', call.case_uuid)
    }
  }

  const handleViewDetails = (call: TCall) => {
    markCallAsViewed(call)
    router.push(`/app/calls/${call.uuid}`)
  }

  const getStatusColor = (result: CustomerCallResult) => {
    if (!result) return 'bg-gray-50 text-gray-700 ring-gray-600/20'

    switch (result.classification) {
      case ClassificationEnum.EXISTING_CLIENT_CALL_BACK:
        return 'bg-purple-50 text-purple-700 ring-purple-600/20'

      case ClassificationEnum.NEW_CLIENT_CALL:
        return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'

      case ClassificationEnum.GENERAL_ENQUIRY:
        return 'bg-sky-50 text-sky-700 ring-sky-600/20'

      case ClassificationEnum.QUESTION:
        return 'bg-amber-50 text-amber-700 ring-amber-600/20'

      case ClassificationEnum.NOT_COMPLETED:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'

      case ClassificationEnum.LAWFIRM_PARTNER:
        return 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'

      case ClassificationEnum.WITHOUT_MESSAGE:
        return 'bg-red-50 text-red-700 ring-red-600/20'

      case ClassificationEnum.COURT:
        return 'bg-red-100 text-red-800 ring-red-700/30'

      case ClassificationEnum.SPAM:
        return 'bg-gray-100 text-gray-800 ring-gray-700/30'

      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'
    }
  }

  const classificationLabels = {
    EXISTING_CLIENT_CALL_BACK: `${t('ExistingClient')}`,
    NEW_CLIENT_CALL: `${t('NewClient')}`,
    GENERAL_ENQUIRY: `${t('GeneralEnquiry')}`,
    QUESTION: `${t('Question')}`,
    NOT_COMPLETED: `${t('NotCompleted')}`,
    LAWFIRM_PARTNER: `${t('LawFirmPartner')}`,
    CALL_FORWARDED: `${t('CallForwarded')}`,
    WITHOUT_MESSAGE: `${t('WithoutMessage')}`,
    COURT: `${t('Court')}`,
    SPAM: `${t('Spam')}`
  }

  type ClassificationType = keyof typeof classificationLabels

  const getClassificationLabel = (classification: ClassificationType | undefined) => {
    if (!classification) return t('Unknown')
    const resp = classificationLabels[classification]
    return resp
  }

  // Direct deletion function
  const deleteCall = async (call: TCall) => {
    try {
      await CallsService.callsDestroy({
        uuid: call.uuid
      })

      // Refresh the table data
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error deleting call:', error)
    }
  }

  const isNewCall = (call: TCall) => call.status === 'open' || call.status === 'in_progress'

  const getCallClientName = (call: TCall): string => {
    const client = call.result?.data?.client as any
    if (!client) return ''
    const first = client.first_name?.trim() || ''
    const last = client.last_name?.trim() || ''
    const person = `${first} ${last}`.trim()
    return client.company_name || client.institution_name || person
  }

  const toInquiryTooltip = (call: TCall): string => {
    const name = getCallClientName(call)
    return name ? t('toInquiryNamed', { name }) : t('toInquiry')
  }

  const createInquiryForCall = async (call: TCall) => {
    try {
      const response = await CallsService.callsCreateCaseCreate({ uuid: call.uuid })
      if (response?.case_uuid) {
        trackEvent(MetricEventType.PHONE_AI_INQUIRY_CREATED, {
          entry_point: 'context_menu',
          call_uuid: call.uuid,
          case_uuid: response.case_uuid
        })
        window.location.href = ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(
          ':uuid',
          response.case_uuid
        )
      } else if (tableRef.value) {
        tableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error creating case from call:', error)
      genericErrorToast()
    }
  }

  const setCallStatus = async (call: TCall, status: 'open' | 'handled') => {
    try {
      await CallsService.callsPartialUpdate({
        uuid: call.uuid,
        requestBody: { status }
      })
      await fetchTabCounts()
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error updating call status:', error)
      genericErrorToast()
    }
  }

  const completeCall = (call: TCall) => setCallStatus(call, 'handled')
  const markCallAsNew = (call: TCall) => setCallStatus(call, 'open')

  // get clerks for the call. Case clerks are preferred
  const getCallClerks = (call: TCall) => {
    const caseClerks = (call as any).case?.clerks
    if (caseClerks && caseClerks.length > 0) return caseClerks
    const callClerks = (call as any).clerks
    if (callClerks && callClerks.length > 0) return callClerks
    // Fallback to single clerk for backward compat
    const singleClerk = call.case?.clerk || call.clerk
    return singleClerk ? [singleClerk] : []
  }

  // Clerk inline editing state
  const updatingClerkForCall = ref<string | null>(null)

  const noteDialogOpen = ref(false)
  const editingNoteForCallUuid = ref<string | null>(null)
  const editNoteText = ref('')
  const editingNoteWasEmptyBefore = ref(true)
  const savingCallNote = ref(false)

  // Shaped for ClerkSelectionPopover — matches the case list/detail pattern.
  const orgClerkOptions = computed<ClerkOption[]>(() =>
    orgUsers.value
      .map((user) => ({
        uuid: user.uuid,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        avatar: user.avatar
      }))
      .sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'de')
      )
  )

  const getCallClerkIds = (call: TCall): string[] =>
    getCallClerks(call).map((c: any) => c.uuid as string)

  const updateCallClerks = async (call: TCall, clerkUuids: string[]) => {
    updatingClerkForCall.value = call.uuid
    try {
      if (call.case_uuid) {
        // Update clerks on the case
        await CasesService.casesPartialUpdate({
          uuid: call.case_uuid,
          requestBody: {
            clerk_ids: clerkUuids
          }
        })
      } else {
        // Update clerks on the call itself
        await CallsService.callsPartialUpdate({
          uuid: call.uuid,
          requestBody: {
            clerk_ids: clerkUuids
          }
        })
      }
      // Update local state
      const newClerks = clerkUuids
        .map((uuid) => {
          const user = orgUsers.value.find((u) => u.uuid === uuid)
          return user
            ? { uuid: user.uuid, first_name: user.first_name, last_name: user.last_name }
            : null
        })
        .filter(Boolean)

      if ((call as any).case) {
        ;(call as any).case.clerks = newClerks
        ;(call as any).case.clerk = newClerks[0] || null
      } else {
        ;(call as any).clerks = newClerks
        ;(call as any).clerk = newClerks[0] || null
      }
    } catch (error) {
      genericErrorToast()
    } finally {
      updatingClerkForCall.value = null
    }
  }

  const openCallNoteDialog = (call: TCall, event: Event) => {
    event.stopPropagation()
    editingNoteForCallUuid.value = call.uuid
    editNoteText.value = call.note || ''
    editingNoteWasEmptyBefore.value = !call.note
    noteDialogOpen.value = true
  }

  const saveCallNote = async () => {
    if (!editingNoteForCallUuid.value) return
    const wasEmptyBeforeUi = editingNoteWasEmptyBefore.value
    const savedCallUuid = editingNoteForCallUuid.value
    savingCallNote.value = true
    try {
      await CallsService.callsPartialUpdate({
        uuid: savedCallUuid,
        requestBody: {
          note: editNoteText.value || null
        }
      })
      noteDialogOpen.value = false
      if (tableRef.value) {
        tableRef.value.updateItem(savedCallUuid, {
          note: editNoteText.value || null
        })
      }
      if (editNoteText.value) {
        // ``was_empty_before_ui`` mirrors the naming used by the backend's
        // ``PHONE_AI_NOTE_ADDED`` event (``was_empty_before_db``). The two observe
        // different states (UI at dialog-open time vs DB at PATCH time) and can
        // legitimately disagree under concurrent edits; the split name keeps the
        // distinction queryable in PostHog.
        trackEvent(MetricEventType.PHONE_AI_NOTE_DIALOG_SAVED, {
          entry_point: 'call_list_dialog',
          call_uuid: savedCallUuid,
          was_empty_before_ui: wasEmptyBeforeUi,
          note_length: editNoteText.value.length
        })
      }
    } catch (error) {
      console.error('Error saving call note:', error)
      toast({
        title: t('noteSaveError'),
        variant: 'destructive'
      })
    } finally {
      savingCallNote.value = false
    }
  }
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen pb-20 pt-5">
      <div class="mx-auto space-y-6">
        <!-- Header -->
        <div class="flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2">
              <h1 class="text-2xl font-semibold text-gray-900">{{ t('Calls') }}</h1>
              <HelpLink
                :url="KNOWLEDGE_BASE_URLS.phoneAi"
                :tooltip="t('learnMorePhoneAi')"
                location="call_list"
              />
            </div>
            <p class="mt-1 text-sm text-gray-500">{{ t('pageDescription') }}</p>
          </div>
          <button
            v-if="!isGermanLocale"
            class="inline-flex items-center gap-2 rounded-md px-1.5 py-1.5 text-sm font-medium text-gray-800
              hover:text-purple-600"
            @click="openSupportDialog('contact')"
          >
            <Mail class="h-5 w-5" />
            {{ t('contactSupport') }}
          </button>
        </div>

        <UsageWarningBanner
          v-if="showPhoneAiUsage && org"
          :used-minutes="org.phonebot_self_service_current_period_used_minutes"
          :included-minutes="phoneAiIncludedMinutes"
          :plan="org.phonebot_self_service_plan"
          :period-end="org.phonebot_self_service_current_period_end"
        />

        <div
          v-if="(showPhoneAiUsage && org) || phoneNumbers.length > 0"
          class="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch lg:gap-6"
        >
          <UsageProgressBar
            v-if="showPhoneAiUsage && org"
            class="min-w-0"
            :used-minutes="org.phonebot_self_service_current_period_used_minutes"
            :included-minutes="phoneAiIncludedMinutes"
            :plan="org.phonebot_self_service_plan"
            @upgrade-plan="openSupportDialog('upgrade')"
          />

          <!-- Phone Numbers Card -->
          <div
            v-if="phoneNumbers.length > 0"
            class="rounded-xl border border-gray-200 bg-white p-4"
          >
            <div class="flex items-center justify-between gap-4">
              <h2 class="text-base font-semibold text-gray-900">
                {{ phoneNumbers.length === 1 ? t('YourPhoneNumber') : t('YourPhoneNumbers') }}
              </h2>
              <div class="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  class="h-8 gap-1.5 border-gray-200 bg-white px-2 text-sm font-normal text-gray-800 hover:bg-gray-50"
                  @click="handleGoToSettings"
                >
                  <Settings class="h-4 w-4" />
                  {{ t('GoToSettings') }}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  class="h-8 gap-1.5 border-gray-200 bg-white px-2 text-sm font-normal text-gray-800 hover:bg-gray-50"
                  @click="openTestCallDialog('test_call_button')"
                >
                  <PhoneCall class="h-4 w-4" />
                  {{ t('TestCall') }}
                </Button>
              </div>
            </div>
            <div class="mt-4 flex flex-wrap items-center gap-4">
              <a
                v-for="phoneNumber in phoneNumbers.slice(0, MAX_VISIBLE_NUMBERS)"
                :key="phoneNumber.uuid"
                :href="`tel:${phoneNumber.phone_number}`"
                class="cursor-pointer text-sm text-gray-900 underline decoration-gray-500 underline-offset-2
                  hover:text-purple-600 hover:decoration-purple-600"
                @click="handleOwnNumberClicked(phoneNumber, 'phone_numbers_card')"
              >
                {{ phoneNumber.phone_number }}
              </a>
              <button
                v-if="phoneNumbers.length > MAX_VISIBLE_NUMBERS"
                class="text-sm font-medium text-purple-600 hover:text-purple-700"
                @click="openTestCallDialog('more_numbers_link')"
              >
                {{
                  t('moreNumbers').replace(
                    '{count}',
                    String(phoneNumbers.length - MAX_VISIBLE_NUMBERS)
                  )
                }}
              </button>
            </div>
          </div>
        </div>

        <!-- Call Status Tabs -->
        <!--
          The new/closed split mirrors the case lifecycle (a call flips to
          HANDLED when ingress auto-creates a case). Phone-AI-only users do
          not own that lifecycle, so we hide the tabs for them and show every
          call in the table instead.
        -->
        <div
          v-if="!isPhoneAiUser"
          class="flex items-center gap-3"
        >
          <button
            :class="[
              `inline-flex h-[44px] w-[299px] items-center gap-2 rounded-lg border px-3 text-sm font-medium
              transition-all`,
              callStatusTab === 'new'
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            ]"
            @click="switchTab('new')"
          >
            <PhonePlusIcon class="h-4 w-4 flex-shrink-0 text-gray-700" />
            <span class="text-gray-600">{{ t('NewCalls') }}</span>
            <span
              v-if="newCallsCount > 0"
              class="h-2 w-2 flex-shrink-0 rounded-full bg-purple-600"
            ></span>
            <span class="ml-auto font-semibold text-gray-900">{{ newCallsCount }}</span>
          </button>
          <button
            :class="[
              `inline-flex h-[44px] w-[299px] items-center gap-2 rounded-lg border px-3 text-sm font-medium
              transition-all`,
              callStatusTab === 'closed'
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            ]"
            @click="switchTab('closed')"
          >
            <PhoneCheckIcon class="h-4 w-4 flex-shrink-0 text-gray-700" />
            <span class="text-gray-600">{{ t('Closed') }}</span>
            <span class="ml-auto font-semibold text-gray-900">{{ closedCallsCount }}</span>
          </button>
        </div>

        <!-- Table -->
        <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5">
          <!-- Show paginated table if not standalone or loading or there are calls or there are no calls and there are filters-->
          <template v-if="showCallListTableSection">
            <!-- Filters Row -->
            <div class="mb-2 flex flex-col gap-2 md:flex-row md:items-center">
              <!-- Search input -->
              <div class="relative w-full md:max-w-[320px] md:flex-1">
                <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="t('search')"
                  class="pl-9"
                  autocomplete="off"
                  name="call-filter-query"
                />
              </div>

              <!-- Filter controls wrapper -->
              <div class="flex flex-wrap items-center gap-2">
                <!-- Type (classification) multi-select dropdown -->
                <Popover v-model:open="typeDropdownOpen">
                  <PopoverTrigger asChild>
                    <button
                      class="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2
                        text-sm hover:bg-gray-50"
                    >
                      <span class="shrink-0 text-gray-500">{{ t('Type') }}:</span>
                      <span class="truncate font-medium text-gray-900">
                        {{ selectedTypeLabel }}
                      </span>
                      <Badge
                        v-if="selectedTypeCount > 1"
                        class="ml-0.5 h-5 min-w-[20px] bg-purple-100 px-1.5 text-xs text-purple-700"
                      >
                        +{{ selectedTypeCount - 1 }}
                      </Badge>
                      <ChevronDown class="h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    class="w-[240px] p-0"
                    align="start"
                  >
                    <div class="p-2">
                      <div class="relative">
                        <Search
                          class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                          v-model="typeDropdownSearch"
                          type="text"
                          :placeholder="t('search')"
                          class="h-8 pl-8 text-sm"
                          autocomplete="off"
                        />
                      </div>
                    </div>
                    <button
                      role="menuitemcheckbox"
                      :aria-checked="
                        allTypesChecked ? 'true' : allTypesIndeterminate ? 'mixed' : 'false'
                      "
                      class="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                      @click="toggleAllTypes"
                    >
                      <span
                        class="flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors"
                        :class="
                          allTypesChecked || allTypesIndeterminate
                            ? 'border-purple-600 bg-purple-600 text-white'
                            : 'border-gray-300 bg-white'
                        "
                      >
                        <Check
                          v-if="allTypesChecked"
                          class="h-3 w-3"
                        />
                        <span
                          v-else-if="allTypesIndeterminate"
                          class="h-0.5 w-2 rounded bg-white"
                        ></span>
                      </span>
                      <span class="truncate">{{ t('AllTypes') }}</span>
                    </button>
                    <div class="max-h-[240px] overflow-y-auto border-t border-gray-100 py-1">
                      <button
                        v-for="option in filteredTypeOptions"
                        :key="option.value"
                        role="menuitemcheckbox"
                        :aria-checked="isTypeSelected(option.value) ? 'true' : 'false'"
                        class="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                        @click="selectType(option.value)"
                      >
                        <span
                          class="flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors"
                          :class="
                            isTypeSelected(option.value)
                              ? 'border-purple-600 bg-purple-600 text-white'
                              : 'border-gray-300 bg-white'
                          "
                        >
                          <Check
                            v-if="isTypeSelected(option.value)"
                            class="h-3 w-3"
                          />
                        </span>
                        <span class="truncate">{{ option.title }}</span>
                      </button>
                    </div>
                    <div
                      v-if="selectedTypeCount > 0"
                      class="border-t border-gray-100 p-2"
                    >
                      <button
                        class="block w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 text-center text-sm
                          text-gray-800 hover:bg-gray-50"
                        @click="clearTypes"
                      >
                        {{ t('clearAll') }}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>

                <!-- Law field filter dropdown -->
                <Popover v-model:open="lawFieldDropdownOpen">
                  <PopoverTrigger asChild>
                    <button
                      class="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2
                        text-sm hover:bg-gray-50"
                    >
                      <span class="shrink-0 text-gray-500">{{ t('LawField') }}:</span>
                      <span class="truncate font-medium text-gray-900">
                        {{ selectedLawFieldLabel }}
                      </span>
                      <ChevronDown class="h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    class="w-[240px] p-0"
                    align="start"
                  >
                    <div class="p-2">
                      <div class="relative">
                        <Search
                          class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                          v-model="lawFieldDropdownSearch"
                          type="text"
                          :placeholder="t('search')"
                          class="h-8 pl-8 text-sm"
                          autocomplete="off"
                        />
                      </div>
                    </div>
                    <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
                      <button
                        v-for="option in filteredLawFieldOptions"
                        :key="option.value"
                        class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                        :class="{ 'bg-gray-50': selectedLawField === option.value }"
                        :title="option.title"
                        @click="selectLawField(option.value)"
                      >
                        <span class="truncate">{{ option.title }}</span>
                        <Check
                          v-if="selectedLawField === option.value"
                          class="h-4 w-4 shrink-0 text-purple-600"
                        />
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>

                <!-- Clerk filter dropdown -->
                <Popover v-model:open="clerkDropdownOpen">
                  <PopoverTrigger asChild>
                    <button
                      class="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2
                        text-sm hover:bg-gray-50"
                    >
                      <span class="shrink-0 text-gray-500">{{ t('Clerk') }}:</span>
                      <span class="truncate font-medium text-gray-900">
                        {{ selectedClerkLabel }}
                      </span>
                      <ChevronDown class="h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    class="w-[240px] p-0"
                    align="start"
                  >
                    <div class="p-2">
                      <div class="relative">
                        <Search
                          class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <Input
                          v-model="clerkDropdownSearch"
                          type="text"
                          :placeholder="t('search')"
                          class="h-8 pl-8 text-sm"
                          autocomplete="off"
                        />
                      </div>
                    </div>
                    <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
                      <button
                        v-for="option in filteredClerkOptions"
                        :key="option.value"
                        class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                        :class="{
                          'bg-gray-50 font-medium text-purple-700': selectedClerk === option.value
                        }"
                        :title="option.title"
                        @click="selectClerk(option.value)"
                      >
                        <span class="truncate">{{ option.title }}</span>
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>

                <!-- Date range filter -->
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      class="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm
                        hover:bg-gray-50"
                    >
                      <Calendar class="h-4 w-4 shrink-0 text-gray-500" />
                      <span class="shrink-0 text-gray-500">{{ t('timeframe') }}:</span>
                      <span class="font-medium text-gray-900">{{ dateRangeText }}</span>
                      <X
                        v-if="dateRange.start || dateRange.end"
                        class="h-4 w-4 shrink-0 rounded-sm text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                        @click.stop="dateRange = { start: undefined, end: undefined }"
                      />
                      <ChevronDown
                        v-else
                        class="h-4 w-4 shrink-0 text-gray-400"
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start">
                    <RangeCalendar v-model="dateRange" />
                  </PopoverContent>
                </Popover>

                <!-- Spacer -->
                <div class="hidden flex-1 md:block"></div>

                <!-- Clear all link -->
                <button
                  class="whitespace-nowrap text-sm text-gray-600 underline hover:text-gray-900"
                  :class="{ invisible: !hasActiveFilters }"
                  @click="clearFilters"
                >
                  {{ t('clearAll') }}
                </button>
              </div>
            </div>

            <PaginatedTableView
              ref="tableRef"
              :fetchData="fetchCalls"
              :show-column-dividers="false"
              header-row-class="[&>td]:whitespace-nowrap [&>td]:text-left [&>td]:align-middle"
              :initial-page="initialPage"
              @page-change="handlePageChange"
            >
              <template #table-head>
                <TableCell class="w-[120px] py-4 pl-8">{{ t('TimeStamp') }}</TableCell>
                <TableCell class="w-[140px] py-4">{{ t('Type') }}</TableCell>
                <TableCell class="w-[260px] py-4">{{ t('Name') }}</TableCell>
                <TableCell class="w-[160px] py-4">{{ t('Phone') }}</TableCell>
                <TableCell class="w-[100px] py-4">
                  <template
                    v-for="(part, i) in t('FieldTopic').split('/')"
                    :key="i"
                  >
                    <div>{{ i === 0 ? part + '/' : part }}</div>
                  </template>
                </TableCell>
                <TableCell class="w-[100px] py-4">{{ t('Duration') }}</TableCell>
                <TableCell class="w-[160px] py-4">{{ t('Clerk') }}</TableCell>
                <TableCell class="w-[200px] min-w-[150px] py-4">{{ t('note') }}</TableCell>
                <TableCell
                  v-if="!isVoiceStandAlone"
                  class="w-[32px] py-4"
                ></TableCell>
                <TableCell class="py-4"></TableCell>
              </template>

              <template #table-row="{ items }">
                <TableRow
                  v-for="call in items"
                  :key="call.uuid"
                  class="group cursor-pointer border-b border-gray-100 hover:bg-gray-50/50"
                  @click="handleViewDetails(call)"
                >
                  <TableCell class="relative whitespace-nowrap py-4 pl-8 text-sm text-gray-600">
                    <span
                      v-if="call.status === 'open'"
                      class="absolute left-2 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-purple-500"
                    ></span>
                    <div>{{ formatDateParts(call.call_start_time).datePart }}</div>
                    <div>{{ formatDateParts(call.call_start_time).timePart }}</div>
                  </TableCell>
                  <TableCell>
                    <Badge :class="`${getStatusColor(call.result)} whitespace-nowrap`">
                      {{ getClassificationLabel(call.result?.classification) || t('Unknown') }}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div class="line-clamp-2 font-medium text-gray-900">
                      {{
                        `${call.result?.data?.client?.first_name ?? ''} ${call.result?.data?.client?.last_name ?? ''}`.trim() ||
                        '_'
                      }}
                    </div>
                  </TableCell>
                  <TableCell class="whitespace-nowrap">
                    <div class="text-sm text-gray-600">
                      {{ call.result?.data?.client?.phone || '-' }}
                    </div>
                  </TableCell>
                  <TableCell class="max-w-[100px] text-xs text-gray-600">
                    <div
                      class="truncate"
                      :title="translateLawField(call.result?.data?.law?.field)"
                    >
                      {{ translateLawField(call.result?.data?.law?.field) }}
                    </div>
                    <div
                      class="truncate"
                      :title="translateLawTopic(call.result?.data?.law?.topic)"
                    >
                      {{ translateLawTopic(call.result?.data?.law?.topic) }}
                    </div>
                  </TableCell>
                  <TableCell class="text-sm text-gray-600">
                    {{ formatDuration(call.duration) }}
                  </TableCell>
                  <TableCell
                    class="w-[200px] max-w-[200px] py-2.5"
                    @click.stop
                  >
                    <!-- Loading state -->
                    <div
                      v-if="updatingClerkForCall === call.uuid"
                      class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1"
                    >
                      <Loader2 class="h-4 w-4 animate-spin text-purple-500" />
                      <span class="text-sm text-gray-400">{{ t('loading') }}</span>
                    </div>
                    <!-- Clerks assigned -->
                    <ClerkSelectionPopover
                      v-else-if="getCallClerks(call).length > 0"
                      :clerk-options="orgClerkOptions"
                      :current-clerk-ids="getCallClerkIds(call)"
                      :max-clerks="3"
                      popover-width="250px"
                      @confirm="(ids) => updateCallClerks(call, ids)"
                    >
                      <button
                        class="flex items-center gap-1.5"
                        @click.stop
                      >
                        <div class="flex -space-x-1.5">
                          <div
                            v-for="clerk in getCallClerks(call)"
                            :key="clerk.uuid"
                            class="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500 text-[10px] font-medium
                              text-white ring-2 ring-white"
                          >
                            {{ getInitials(`${clerk.first_name} ${clerk.last_name}`) }}
                          </div>
                        </div>
                        <span class="min-w-0 truncate text-sm text-gray-900">
                          {{ getCallClerks(call)[0].first_name }}
                        </span>
                      </button>
                    </ClerkSelectionPopover>
                    <!-- No clerks - Add clerk button with popover -->
                    <ClerkSelectionPopover
                      v-else
                      :clerk-options="orgClerkOptions"
                      :current-clerk-ids="[]"
                      :max-clerks="3"
                      popover-width="250px"
                      @confirm="(ids) => updateCallClerks(call, ids)"
                    >
                      <button
                        class="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm
                          text-gray-500 hover:bg-gray-50"
                      >
                        {{ t('addClerk') }}
                      </button>
                    </ClerkSelectionPopover>
                  </TableCell>
                  <TableCell
                    data-cy="cell-call-note"
                    class="w-[200px] max-w-[200px] py-2.5"
                    @click.stop
                  >
                    <div
                      v-if="call.note"
                      data-cy="call-note-preview"
                      :title="call.note"
                      class="line-clamp-2 cursor-pointer break-words text-sm text-gray-600 hover:text-gray-900 hover:underline"
                      @click="openCallNoteDialog(call, $event)"
                    >
                      {{ call.note }}
                    </div>
                    <button
                      v-else
                      data-cy="btn-call-add-note"
                      class="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm
                        text-gray-500 hover:bg-gray-50"
                      @click="openCallNoteDialog(call, $event)"
                    >
                      {{ t('addNote') }}
                    </button>
                  </TableCell>
                  <TableCell
                    v-if="!isVoiceStandAlone"
                    class="w-10 px-0"
                  >
                    <div class="flex items-center justify-center">
                      <!-- Only show indicator when there's a case_uuid -->
                      <Button
                        v-if="call.case_uuid"
                        variant="ghost"
                        size="icon"
                        :title="toInquiryTooltip(call)"
                        class="h-8 w-8 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                        @click.stop="handleJumpToCase(call, 'list_icon')"
                      >
                        <ExternalLink class="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <!-- Dropdown menu for other actions -->
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          class="h-8 w-8"
                          @click.stop
                        >
                          <MoreHorizontal class="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        class="w-[200px]"
                      >
                        <DropdownMenuItem
                          v-if="call.case_uuid"
                          @click="handleJumpToCase(call, 'context_menu')"
                        >
                          <ExternalLink class="mr-2 h-4 w-4" />
                          {{ t('toInquiry') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          v-else
                          @click="createInquiryForCall(call)"
                        >
                          <Plus class="mr-2 h-4 w-4" />
                          {{ t('createInquiry') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          v-if="isNewCall(call)"
                          @click="completeCall(call)"
                        >
                          <PhoneCheckIcon class="mr-2 h-4 w-4" />
                          {{ t('completeCall') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          v-else
                          @click="markCallAsNew(call)"
                        >
                          <PhonePlusIcon class="mr-2 h-4 w-4" />
                          {{ t('markAsNew') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          class="text-red-500 focus:bg-red-50 focus:text-red-600"
                          @click="deleteCall(call)"
                        >
                          <Trash2 class="mr-2 h-4 w-4" />
                          {{ t('Delete') }}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              </template>

              <!-- Custom empty state -->
              <template #empty>
                <div class="mx-auto max-w-md text-center">
                  <div
                    class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"
                  >
                    <Search class="h-6 w-6 text-gray-400" />
                  </div>
                  <p class="text-base font-medium text-gray-900">
                    {{ t('noCallsFound') }}
                  </p>
                  <p class="mt-1 text-sm text-gray-500">
                    <template v-if="hasActiveFilters">
                      {{ t('noResultsWithFilters') }}
                    </template>
                    <template v-else>
                      {{ t('noCallsYet') }}
                    </template>
                  </p>

                  <!-- Clear filters button if filters are active -->
                  <div
                    v-if="hasActiveFilters"
                    class="mt-4"
                  >
                    <button
                      class="text-sm text-purple-600 hover:text-purple-700 hover:underline"
                      @click="clearFilters"
                    >
                      {{ t('resetFilters') }}
                    </button>
                  </div>
                </div>
              </template>
            </PaginatedTableView>
          </template>
          <div
            v-else-if="isVoiceStandAlone && !isLoading"
            class="flex flex-col items-center justify-center py-16"
          >
            <!-- Illustration -->
            <CustomerService class="mb-8 h-64 w-64" />

            <!-- Title -->
            <h2 class="mb-3 text-center text-xl font-semibold text-gray-900">
              {{ t('emptyStateTitle') }}
            </h2>

            <!-- Description -->
            <p
              class="mb-8 max-w-md whitespace-pre-line text-center text-base font-normal leading-6 tracking-normal
                text-gray-600"
            >
              {{ t('emptyStateDescription') }}
            </p>

            <!-- Setup Button -->
            <Button
              size="lg"
              class="bg-purple-600 hover:bg-purple-700"
              @click="handlePhonebotSetup"
            >
              {{ t('setupNow') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </SpaCompatComponent>

  <!-- Contact support / Upgrade plan dialog -->
  <Dialog
    :open="supportDialogOpen"
    @update:open="supportDialogOpen = $event"
  >
    <DialogContent class="max-w-md p-6 sm:max-w-md">
      <DialogHeader>
        <DialogTitle class="text-lg font-semibold text-gray-900">
          {{ supportDialogMode === 'upgrade' ? t('upgradePlanTitle') : t('contactSupportTitle') }}
        </DialogTitle>
        <DialogDescription class="text-sm text-gray-600">
          <template
            v-for="(part, i) in (supportDialogMode === 'upgrade'
              ? t('upgradePlanDescription')
              : t('contactSupportDescription')
            ).split('{email}')"
            :key="i"
          >
            {{ part }}
            <a
              v-if="i === 0"
              :href="`mailto:${SUPPORT_EMAIL}`"
              class="font-medium text-gray-900 underline"
            >
              {{ SUPPORT_EMAIL }}
            </a>
          </template>
        </DialogDescription>
      </DialogHeader>
      <div class="mt-4 flex items-center gap-3">
        <Button
          variant="secondary"
          class="h-10 flex-1 gap-2 border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
          @click="copySupportEmail"
        >
          <Check
            v-if="supportEmailCopied"
            class="h-4 w-4"
          />
          <Copy
            v-else
            class="h-4 w-4"
          />
          {{ supportEmailCopied ? t('copyEmailDone') : t('copyEmail') }}
        </Button>
        <Button
          class="h-10 flex-1"
          @click="openSupportMailApp"
        >
          {{ t('openEmailApp') }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>

  <CallListNoteDialog
    :open="noteDialogOpen"
    :note-text="editNoteText"
    :saving="savingCallNote"
    :dialog-title="t('editNote')"
    :placeholder="t('enterNote')"
    :save-label="t('save')"
    :cancel-label="t('cancel')"
    @update:open="noteDialogOpen = $event"
    @update:note-text="editNoteText = $event"
    @save="saveCallNote"
  />

  <!-- Setup Phonebot Dialog -->
  <PhonebotSetupDialog
    v-if="isVoiceStandAlone"
    :total-calls="totalCalls"
    @setup="handlePhonebotSetup"
  />

  <!-- Phone Numbers Dialog -->
  <Dialog
    :open="showPhoneNumbersDialog"
    @update:open="showPhoneNumbersDialog = $event"
  >
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>
          {{ phoneNumbers.length === 1 ? t('YourPhoneNumber') : t('YourPhoneNumbers') }}
        </DialogTitle>
        <DialogDescription>
          {{ t('phoneDialogDescription') }}
        </DialogDescription>
      </DialogHeader>
      <div class="grid grid-cols-2 gap-3">
        <div
          v-for="phoneNumber in phoneNumbers"
          :key="phoneNumber.uuid"
          class="rounded-lg border border-gray-200 p-4"
        >
          <div class="text-sm text-gray-500">
            {{ phoneNumber.lawfirm_name || '' }}
          </div>
          <a
            :href="`tel:${phoneNumber.phone_number}`"
            class="text-base font-semibold text-gray-900 underline"
            @click="handleOwnNumberClicked(phoneNumber, 'test_call_dialog')"
          >
            {{ phoneNumber.phone_number }}
          </a>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
